import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  buildDraftKey,
  clearConfirmedRowsFromDraft,
  clearDraft,
  keyForGroup as computeKeyForGroup,
  loadDraft,
  purgeStaleDrafts,
  releaseGroupKey,
  saveDraft,
} from '../utils/encodingDraft';

// Long enough that a burst of typing writes once, not per keystroke; short
// enough that a browser crash costs at most a line or two.
const DEBOUNCE_MS = 1500;

/**
 * Browser-local autosave for a tax encoding grid. One hook, used by both VAT
 * forms and the Percentage Tax form — the screens differ in how they submit,
 * not in what needs protecting.
 *
 * The caller owns its rows; this hook only mirrors them to localStorage,
 * surfaces a draft found on mount so the screen can prompt before the user
 * types over it, and — after a submit — forgets ONLY the rows the server
 * confirmed.
 *
 * @param {string}   screen      one of DRAFT_SCREENS
 * @param {string}   clientId    accounting client being encoded for
 * @param {number}   fiscalYear  filing year the rows belong to
 * @param {Array}    rows        current grid state
 * @param {Function} isRowEmpty  the form's own emptiness test, reused verbatim
 *                               so "nothing typed yet" means the same thing to
 *                               autosave as it does to save
 */
export default function useEncodingDraft({ screen, clientId, fiscalYear, rows, isRowEmpty }) {
  const { user } = useAuth();
  // The codebase is inconsistent about which id field is populated (_id on the
  // fetched user, id on the JWT-derived one) — take whichever is there.
  const userId = user?._id || user?.id || null;

  const identity = useMemo(
    () => ({ clientId, userId, screen, fiscalYear }),
    [clientId, userId, screen, fiscalYear]
  );

  const draftKey = useMemo(() => buildDraftKey(identity), [identity]);

  const [pendingDraft, setPendingDraft] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const timerRef = useRef(null);
  // While the restore prompt is open the grid still holds its empty starting
  // rows — writing those would overwrite the very draft we are offering.
  const promptOpenRef = useRef(false);
  const submitKeysRef = useRef({});
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Load-on-mount (and whenever the identity changes — switching client, year
  // or tab is a different draft). Purge first so an expired draft is never
  // offered.
  useEffect(() => {
    setPendingDraft(null);
    setLastSavedAt(null);
    submitKeysRef.current = {};

    if (!draftKey) return;

    purgeStaleDrafts();

    const existing = loadDraft(draftKey, identity);
    if (!existing) return;

    const filled = existing.rows.filter((row) => !isRowEmptySafe(isRowEmpty, row));
    if (filled.length === 0) {
      // A draft with nothing worth restoring — drop it silently rather than
      // interrupting the user with an empty prompt.
      clearDraft(draftKey);
      return;
    }

    submitKeysRef.current = existing.submitKeys || {};
    promptOpenRef.current = true;
    setPendingDraft({ ...existing, filledCount: filled.length });
    setLastSavedAt(existing.savedAt);
    // isRowEmpty is a stable module-scope helper in every caller; identity is
    // what actually re-opens a different draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, identity]);

  // Debounced mirror of the current rows.
  useEffect(() => {
    if (!draftKey) return undefined;
    if (promptOpenRef.current) return undefined;

    const hasContent = (rows || []).some((row) => !isRowEmptySafe(isRowEmpty, row));
    if (!hasContent) return undefined;

    setIsSaving(true);
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const written = saveDraft(draftKey, {
        ...identity,
        rows: rowsRef.current,
        submitKeys: submitKeysRef.current,
      });
      setIsSaving(false);
      if (written) setLastSavedAt(written.savedAt);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, draftKey, identity]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  /** User chose Restore — hand the stored rows back to the form. */
  const restoreDraft = useCallback(() => {
    const restored = pendingDraft?.rows || null;
    promptOpenRef.current = false;
    setPendingDraft(null);
    return restored;
  }, [pendingDraft]);

  /** User chose Discard — the draft goes, the grid keeps its empty rows. */
  const discardDraft = useCallback(() => {
    promptOpenRef.current = false;
    setPendingDraft(null);
    setLastSavedAt(null);
    submitKeysRef.current = {};
    clearDraft(draftKey);
  }, [draftKey]);

  /**
   * Forgets only the rows the server confirmed. Anything failed or unknown
   * stays in storage so a refresh — or a retry — still has it.
   */
  const clearConfirmedRows = useCallback(
    (confirmedRowIds) => {
      if (!draftKey) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const remaining = clearConfirmedRowsFromDraft(
        draftKey,
        confirmedRowIds,
        identity,
        // The authority on submit keys is memory, not storage: keys issued
        // during the submit that just failed may not have been flushed yet, and
        // the retry has to replay them.
        submitKeysRef.current
      );

      setIsSaving(false);
      setLastSavedAt(remaining ? remaining.savedAt : null);

      // Submit keys are deliberately NOT cleared here. A confirmed group
      // releases its own key (releaseKey), while a group still awaiting a retry
      // must keep its key to replay — including the case where no draft had
      // been flushed to storage yet, which is precisely when someone typed and
      // hit Save inside the debounce window.
    },
    [draftKey, identity]
  );

  /**
   * Stable Idempotency-Key for one submit group, persisted with the draft so it
   * survives the refresh between a dropped response and the retry.
   *
   * Returns { key, stamp }. Use `stamp` for any timestamp the request body
   * carries: minting a fresh `new Date()` per attempt would change the body
   * under an unchanged key, which the backend rejects as key reuse.
   */
  const keyForGroup = useCallback((groupId, payload) => {
    const { key, stamp, submitKeys } = computeKeyForGroup(submitKeysRef.current, groupId, payload);
    submitKeysRef.current = submitKeys;
    return { key, stamp };
  }, []);

  /** Called once a group is confirmed saved, so its key is not replayed again. */
  const releaseKey = useCallback((groupId) => {
    submitKeysRef.current = releaseGroupKey(submitKeysRef.current, groupId);
  }, []);

  return {
    pendingDraft,
    restoreDraft,
    discardDraft,
    clearConfirmedRows,
    keyForGroup,
    releaseKey,
    lastSavedAt,
    isSaving,
    hasDraft: Boolean(lastSavedAt),
  };
}

// A form's isRowEmpty throws if handed a shape it doesn't recognize (a draft
// from a grid that has since changed). Treat anything it can't judge as empty
// rather than letting it break the mount.
const isRowEmptySafe = (isRowEmpty, row) => {
  if (typeof isRowEmpty !== 'function') return false;
  try {
    return isRowEmpty(row);
  } catch {
    return true;
  }
};
